import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { getApiBaseUrl } from '../request/helper';

const DEFAULT_ROOM_OWNER = 'default';

type SocketAvailableListener = (socket: Socket) => void;

const SOCKET_PATH = '/socket.io/notifications/';

/**
 * Notification Socket
 * 包含支付、网站等业务相关的实时通知
 */
class NotificationSocket {
  private static instance: NotificationSocket;
  private socket?: Socket;
  private currentUserId?: string;
  private readonly roomOwners = new Map<string, Set<string>>();
  private readonly socketAvailableListeners = new Set<SocketAvailableListener>();

  private constructor() {}

  static getInstance(): NotificationSocket {
    if (!NotificationSocket.instance) {
      NotificationSocket.instance = new NotificationSocket();
    }
    return NotificationSocket.instance;
  }

  initialize(userId: string): void {
    // Payment, website and system-notification modules can initialize during
    // the same render. Reuse the in-flight socket as well as a connected one;
    // recreating it while connecting drops listeners and room ownership.
    if (this.socket && this.currentUserId === userId) {
      return;
    }

    if (this.socket) {
      this.disconnect();
    }

    this.currentUserId = userId;
    this.socket = io(getApiBaseUrl(), {
      path: SOCKET_PATH,
      query: { userId },
      transports: ['websocket', 'polling'],
    });
    this.socket.on('connect', this.handleConnect);
    const socket = this.socket;
    this.socketAvailableListeners.forEach(listener => listener(socket));
  }

  getSocket(): Socket | undefined {
    return this.socket;
  }

  onSocketAvailable(listener: SocketAvailableListener): () => void {
    this.socketAvailableListeners.add(listener);
    if (this.socket) {
      listener(this.socket);
    }

    return () => {
      this.socketAvailableListeners.delete(listener);
    };
  }

  joinRoom(roomId: string, owner = DEFAULT_ROOM_OWNER): void {
    const owners = this.roomOwners.get(roomId) ?? new Set<string>();
    const shouldJoin = owners.size === 0;
    owners.add(owner);
    this.roomOwners.set(roomId, owners);

    if (shouldJoin && this.socket?.connected) {
      this.emitRoomOperation(roomId, 'join');
    }
  }

  leaveRoom(roomId: string, owner = DEFAULT_ROOM_OWNER): void {
    const owners = this.roomOwners.get(roomId);
    if (!owners) {
      return;
    }

    owners.delete(owner);
    if (owners.size > 0) {
      return;
    }

    this.roomOwners.delete(roomId);
    if (this.socket?.connected) {
      this.emitRoomOperation(roomId, 'leave');
    }
  }

  disconnect(): void {
    this.socket?.off('connect', this.handleConnect);
    this.socket?.disconnect();
    this.socket?.removeAllListeners();
    this.socket = undefined;
    this.currentUserId = undefined;
    this.roomOwners.clear();
  }

  get userId(): string | undefined {
    return this.currentUserId;
  }

  private handleConnect = (): void => {
    this.roomOwners.forEach((_, roomId) => this.emitRoomOperation(roomId, 'join'));
  };

  private emitRoomOperation(roomId: string, operation: 'join' | 'leave'): void {
    this.socket?.emit('userRoom', { odaId: roomId, operation });
  }
}

export default NotificationSocket.getInstance();
